import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchWithSecrets } from "../../utils/addressSecrets";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { HiCheckCircle, HiExclamation, HiXCircle } from "react-icons/hi";
import { AlertMessage, AlertTone } from "../../components/atoms/AlertMessage";
import { CustomButton } from "../../components/atoms/CustomButton";
import { CustomInput } from "../../components/atoms/CustomInput";
import EndUserPublicLayout from "../../components/atoms/EndUserPublicLayout";
import { Spinner } from "../../components/atoms/Spinner";
import StepList, { Step } from "../../components/atoms/StepList";
import {
  usePassLessConfirmationContext,
  useConfirmPassLessSecretWord,
} from "../../hooks/auth/passwordless";

type Refusal = { key: string; retryable: boolean };

const REFUSALS: Record<string, Refusal> = {
  RSUSKO015: { key: "noAccount", retryable: false },
  RSUSKO016: { key: "differentClient", retryable: false },
  RSUSKO019: { key: "noRequest", retryable: false },
  RSUSKO021: { key: "expired", retryable: false },
  RSUSKO023: { key: "expired", retryable: false },
  RSUSKO024: { key: "linkInvalid", retryable: false },
  RSUSKO025: { key: "wrongWord", retryable: true },
  RSUSKO043: { key: "tooManyAttempts", retryable: false },
};

const UNKNOWN: Refusal = { key: "default", retryable: false };

const unreachable = (stage: "read" | "confirm"): Refusal => ({
  key: stage === "read" ? "unreachable" : "network",
  retryable: stage === "confirm",
});

const refusalOf = (error: any, stage: "read" | "confirm"): Refusal => {
  if (!error?.response) {
    return unreachable(stage);
  }
  const code = error?.response?.data?.code ?? error?.data?.code;
  return (code && REFUSALS[code]) || UNKNOWN;
};

const TONES: Record<string, { tone: AlertTone; Icon: typeof HiXCircle }> = {
  network: { tone: "warning", Icon: HiExclamation },
  unreachable: { tone: "warning", Icon: HiExclamation },
};

const PassLessConfirmation = () => {
  const { t } = useTranslation("clientView");
  const params = useSearchWithSecrets();

  const entity_identification = params.get("entity_identification") || "";
  const user_identification = params.get("user_identification") || "";
  const token = params.get("token") || "";
  const hasTarget = Boolean(
    entity_identification && user_identification && token
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<{ secret_word: string }>({
    defaultValues: { secret_word: "" },
  });

  const wordInput = useRef<HTMLInputElement>(null);

  const [context, setContext] = useState<any>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [stage, setStage] = useState<
    "reading" | "form" | "closed" | "confirmed"
  >(hasTarget ? "reading" : "closed");

  const { mutateAsync: readContext } = usePassLessConfirmationContext();
  const { mutate: confirmWord, isLoading: isConfirming } =
    useConfirmPassLessSecretWord();

  const hasRead = useRef(false);

  const refuse = useCallback((error: any, at: "read" | "confirm") => {
    const element = error?.response?.data?.element;
    if (element?.email) {
      setContext((current: any) => current ?? element);
    }
    return refusalOf(error, at);
  }, []);

  useEffect(() => {
    if (!hasTarget) {
      setRefusal({ key: "noRequest", retryable: false });
      return;
    }
    if (hasRead.current) {
      return;
    }
    hasRead.current = true;

    readContext({ entity_identification, user_identification, token })
      .then((response: any) => {
        setContext(response?.data?.element || {});
        setStage("form");
      })
      .catch((error) => {
        setRefusal(refuse(error, "read"));
        setStage("closed");
      });
  }, [
    hasTarget,
    entity_identification,
    user_identification,
    token,
    readContext,
    refuse,
  ]);

  const onConfirm = ({ secret_word }: { secret_word: string }) => {
    const word = (secret_word || "").trim();
    if (!word || isConfirming) {
      return;
    }

    setRefusal(null);
    confirmWord(
      { entity_identification, user_identification, token, secret_word: word },
      {
        onSuccess: () => setStage("confirmed"),
        onError: (error) => {
          const next = refuse(error, "confirm");
          setRefusal(next);
          reset({ secret_word: "" });
          if (next.retryable) {
            wordInput.current?.focus();
          } else {
            setStage("closed");
          }
        },
      }
    );
  };

  const email = context?.email;
  const confirmed = stage === "confirmed";

  const steps: Step[] = [
    {
      status: "done",
      title: t("passLessConfirmation.steps.started.title"),
      description: t("passLessConfirmation.steps.started.description"),
    },
    {
      status: "done",
      title: t("passLessConfirmation.steps.wordSet.title"),
      description: t("passLessConfirmation.steps.wordSet.description"),
    },
    {
      status: "done",
      title: t("passLessConfirmation.steps.linkOpened.title"),
      description: t("passLessConfirmation.steps.linkOpened.description"),
    },
    {
      status: "current",
      title: t("passLessConfirmation.steps.confirm.title"),
      description: t("passLessConfirmation.steps.confirm.description"),
    },
    {
      status: "next",
      title: t("passLessConfirmation.steps.signedIn.title"),
      description: t("passLessConfirmation.steps.signedIn.description"),
    },
  ];

  const status = confirmed
    ? {
        tone: "success" as AlertTone,
        Icon: HiCheckCircle,
        title: t("passLessConfirmation.success.title"),
        message: t("passLessConfirmation.success.message"),
      }
    : refusal
    ? {
        tone: TONES[refusal.key]?.tone ?? ("failure" as AlertTone),
        Icon: TONES[refusal.key]?.Icon ?? HiXCircle,
        title: t(`passLessConfirmation.refusals.${refusal.key}.title`),
        message: t(`passLessConfirmation.refusals.${refusal.key}.message`),
      }
    : null;

  return (
    <>
      <Helmet>
        <title>{t("passLessConfirmation.title")} | Super User Manager</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <EndUserPublicLayout
        title={
          confirmed ? undefined : (t("passLessConfirmation.title") as string)
        }
        subtitle={
          confirmed ? undefined : (t("passLessConfirmation.intro") as string)
        }
      >
        {stage === "reading" ? (
          <div className="flex items-center justify-center gap-3 py-6 text-sm text-gray-500">
            <Spinner size="md" />
            {t("passLessConfirmation.loading")}
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {!confirmed && email ? (
              <div className="text-center">
                <p className="text-xs text-gray-500" dir="auto">
                  {t("passLessConfirmation.signingInAs")}
                </p>
                <p
                  className="mt-0.5 truncate text-sm font-medium text-gray-900"
                  dir="ltr"
                  title={email}
                >
                  {email}
                </p>
              </div>
            ) : null}

            {status ? (
              <AlertMessage color={status.tone} icon={status.Icon}>
                <span className="block font-semibold">{status.title}</span>
                <span className="mt-0.5 block">{status.message}</span>
              </AlertMessage>
            ) : null}

            {stage === "form" ? (
              <>
                <form
                  className="flex flex-col gap-4 border-t border-gray-200 pt-5"
                  onSubmit={handleSubmit(onConfirm)}
                >
                  <CustomInput
                    ref={wordInput}
                    name="secret_word"
                    control={control}
                    error={errors.secret_word}
                    type="password"
                    inputDir="ltr"
                    autoComplete="off"
                    autoCapitalize="characters"
                    spellCheck={false}
                    autoFocus
                    label={t("passLessConfirmation.wordLabel").toString()}
                    helperText={
                      errors.secret_word
                        ? (t("passLessConfirmation.wordRequired") as string)
                        : ""
                    }
                    rules={{ required: true, minLength: 3, maxLength: 130 }}
                  />
                  <p className="text-xs text-gray-500" dir="auto">
                    {t("passLessConfirmation.wordHelp")}
                  </p>
                  <CustomButton
                    variant="primary"
                    size="md"
                    type="submit"
                    className="w-full"
                    title={t("passLessConfirmation.confirmBtn")}
                    isLoading={isConfirming}
                    disabled={isConfirming}
                  />
                </form>

                <StepList
                  steps={steps}
                  className="border-t border-gray-200 pt-5"
                />
              </>
            ) : null}
          </div>
        )}
      </EndUserPublicLayout>
    </>
  );
};

export default PassLessConfirmation;
